import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Euro, BedDouble, Bath, Maximize, MapPin, Building2 } from 'lucide-react';
import { getPropertyPriceLabel, hasPublicPrice } from '../../utils/propertyPrice';
import { PLACEHOLDER_IMAGE, getWatermarkedImageUrl } from '../../utils/imageProxy';
import { isLandPropertyType } from '../../utils/propertyApplicability';

interface PropertyCardProps {
  property: {
    id: number;
    reference_number?: string;
    title: string;
    type: 'vendita' | 'affitto';
    price: number | null;
    price_on_request?: boolean;
    location: string;
    bedrooms: number;
    bathrooms?: number | null;
    type_id?: string | null;
    property_types?: { id?: string; code?: string | null; name?: string } | null;
    size: number;
    energy_class?: string;
    property_images?: Array<{ url: string; is_primary: boolean; position?: number }>;
    primaryImage?: string;
    images?: string[];
  };
  onFavoriteClick?: (id: number) => void;
  isFavorite?: boolean;
  index?: number;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  onFavoriteClick, 
  isFavorite = false,
  index = 0 
}) => {
  const isLand = isLandPropertyType(property.property_types || { id: property.type_id });
  const showBathrooms = !isLand || (property.bathrooms ?? 0) > 0;
  // Ottieni l'immagine principale o la prima disponibile.
  // La card mostra la foto a ~700px: chiediamo quella misura con watermark.
  const CARD_IMAGE_WIDTH = 700;

  const getPropertyImage = () => {
    // Se abbiamo property_images, usa quelle
    if (property.property_images && property.property_images.length > 0) {
      // Ordina le immagini: priorità a is_primary, poi per posizione
      const sortedImages = [...property.property_images].sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return (a.position || 0) - (b.position || 0);
      });

      return getWatermarkedImageUrl(sortedImages[0].url, CARD_IMAGE_WIDTH);
    }

    // Altrimenti, usa primaryImage se disponibile
    if (property.primaryImage) {
      return getWatermarkedImageUrl(property.primaryImage, CARD_IMAGE_WIDTH);
    }

    // Oppure usa la prima immagine dall'array images se disponibile
    if (property.images && property.images.length > 0) {
      return getWatermarkedImageUrl(property.images[0], CARD_IMAGE_WIDTH);
    }

    // Immagine di fallback se nessuna delle precedenti è disponibile
    return PLACEHOLDER_IMAGE;
  };

  // Get energy class color
  const getEnergyClassColor = (energyClass?: string) => {
    try {
      if (!energyClass || typeof energyClass !== 'string') {
        return 'bg-gray-500';
      }
      
      const validClasses = ['A4', 'A3', 'A2', 'A1', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
      const upperClass = energyClass.toUpperCase().trim();
      
      switch(upperClass) {
        case 'A4':
        case 'A3':
        case 'A2':
        case 'A1':
        case 'A':
          return 'bg-green-500';
        case 'B': return 'bg-green-600';
        case 'C': return 'bg-yellow-500';
        case 'D': return 'bg-yellow-600';
        case 'E': return 'bg-orange-500';
        case 'F': return 'bg-red-500';
        case 'G': return 'bg-red-600';
        default: return 'bg-gray-500';
      }
    } catch (error) {
      return 'bg-gray-500';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
    >
      <Link to={`/immobile/${property.id}`} className="block h-full">
        <div className="property-box bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all h-full flex flex-col">
          <div className="relative h-64 overflow-hidden">
            <img
              src={getPropertyImage()}
              alt={property.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
              decoding="async"
              width="400"
              height="300"
              onError={(e) => {
                // Fallback se l'immagine non si carica
                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
              }}
            />
            <div className="absolute top-0 left-0 w-full p-3 flex justify-between items-start">
              <span className={`px-3 py-1.5 rounded-md text-sm font-bold ${
                property.type === 'vendita' 
                  ? 'bg-primary text-white' 
                  : 'bg-green-600 text-white'
              }`}>
                {property.type === 'vendita' ? 'Vendita' : 'Affitto'}
              </span>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0">
              <div className="bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                <div className="flex items-center text-white">
                  <MapPin className="h-4 w-4 mr-1 text-secondary flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{property.location}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 flex flex-col flex-grow">
            <div className="flex items-center mb-1 text-xs font-semibold text-gray-500 uppercase">
              <Building2 className="h-3.5 w-3.5 mr-1 text-gray-400" />
              <span>Rif. {property.reference_number || `MOL${String(property.id).slice(-4).toUpperCase()}`}</span>
            </div>
            
            <h3 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
              {property.title}
            </h3>
            
            <div className="mt-auto">
              <div className={`grid ${showBathrooms ? 'grid-cols-3' : 'grid-cols-2'} gap-1 mb-3`}>
                <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                  <BedDouble className="h-4 w-4 text-primary mb-1" />
                  <span className="text-xs font-medium">{property.bedrooms} {property.bedrooms === 1 ? 'Camera' : 'Camere'}</span>
                </div>
                {showBathrooms && <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                  <Bath className="h-4 w-4 text-primary mb-1" />
                  <span className="text-xs font-medium">{property.bathrooms} {property.bathrooms === 1 ? 'Bagno' : 'Bagni'}</span>
                </div>}
                <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                  <Maximize className="h-4 w-4 text-primary mb-1" />
                  <span className="text-xs font-medium">{property.size} m²</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">
                    {property.type === 'vendita' ? 'Prezzo' : 'Canone mensile'}
                  </span>
                  <div className="flex items-center text-primary font-bold text-xl leading-none">
                    {getPropertyPriceLabel(property)}
                    {hasPublicPrice(property) && property.type === 'affitto' && <span className="text-xs font-normal text-gray-500 ml-1">/mese</span>}
                  </div>
                </div>
                
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-500 mb-0.5">Classe energetica</span>
                  <div className={`px-2 py-1 rounded text-xs font-bold text-white ${getEnergyClassColor(property.energy_class)}`}>
                    {property.energy_class || 'N.D.'}
                  </div>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                <span className="bg-secondary/10 hover:bg-secondary text-secondary hover:text-white px-4 py-1.5 rounded-md text-sm font-semibold transition-colors inline-flex items-center">
                  Scopri di più
                  <svg className="w-3.5 h-3.5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
